import { useQuery } from '@tanstack/react-query'
import { Link, useLocation } from 'react-router-dom'
import { Alert } from '@/components/ui/alert'
import { getJSON } from '@/lib/api'

type PreviewField = { key: string; label: string; value: unknown }
type Preview = {
  rendering: 'members' | 'public'
  available: boolean
  message?: string
  member?: { name: string; fields: PreviewField[] }
}

export function MemberPortalDirectoryPreview() {
  const rendering =
    new URLSearchParams(useLocation().search).get('rendering') === 'public' ? 'public' : 'members'
  const preview = useQuery<{ member_directory_preview: Preview }>({
    queryKey: ['member-directory-preview', rendering],
    queryFn: () => getJSON(`/api/v1/member_directory_preview?rendering=${rendering}`),
    retry: false,
  })
  if (preview.isLoading)
    return <p className="mt-6 text-body-sm text-muted-foreground">Loading preview…</p>
  const value = preview.data?.member_directory_preview
  if (!value?.available)
    return <Alert className="mt-6">{value?.message ?? 'This preview is unavailable.'}</Alert>
  return (
    <div className="mt-3 max-w-2xl space-y-3">
      <Link
        to="/portal/profile/directory"
        className="text-body-sm text-muted-foreground hover:text-foreground"
      >
        ← Directory listing
      </Link>
      <h1 className="text-subheading font-semibold">
        {rendering === 'public' ? 'Public preview' : 'Member preview'}
      </h1>
      <div className="overflow-hidden rounded border border-border bg-card">
        <h2 className="border-b border-border px-5 py-3 text-body font-medium">
          {value.member?.name}
        </h2>
        {value.member?.fields.map((field) => (
          <div
            key={field.key}
            className="flex gap-4 border-b border-border px-5 py-2 last:border-b-0"
          >
            <span className="w-40 shrink-0 text-caption text-muted-foreground">{field.label}</span>
            <PreviewValue field={field} />
          </div>
        ))}
      </div>
    </div>
  )
}

function PreviewValue({ field }: { field: PreviewField }) {
  if (Array.isArray(field.value))
    return <span className="text-body-sm">{field.value.join(', ')}</span>
  if (typeof field.value === 'object' && field.value !== null && 'thumbnail_url' in field.value) {
    return (
      <img
        src={String(field.value.thumbnail_url)}
        alt=""
        className="size-10 rounded object-cover"
      />
    )
  }
  return <span className="text-body-sm">{String(field.value)}</span>
}
